import axios from 'axios';

/**
 * Staff notes: Safety Rules and Closing Protocol.
 *
 * The text is kept in a light format a site admin can edit in a plain box:
 *   # Part heading
 *   ## Section heading
 *   - A rule
 * Anything else is an ordinary paragraph. The page and the PDF both read it
 * through parseStaffNotes, so what is on screen is what gets printed.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '') || '';

export const STAFF_NOTES_TITLE = 'Safety Rules and Closing Protocol';

export interface StaffNotesVersion {
  version: number;
  content: string;
  change_note: string;
  changed_by_name: string;
  created_at: string;
}

export interface StaffNotesBlock {
  kind: 'part' | 'section' | 'rule' | 'text';
  text: string;
}

export const staffAuthHeaders = () => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('operatorToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchStaffNotes = async (): Promise<StaffNotesVersion> => {
  const res = await axios.get(`${BACKEND}/v1/admin/staff-notes`, { headers: staffAuthHeaders() });
  return res.data?.data?.notes as StaffNotesVersion;
};

export const fetchStaffNoteVersions = async (): Promise<StaffNotesVersion[]> => {
  const res = await axios.get(`${BACKEND}/v1/admin/staff-notes/versions`, { headers: staffAuthHeaders() });
  return (res.data?.data?.versions ?? []) as StaffNotesVersion[];
};

export const saveStaffNotes = async (input: {
  content: string;
  change_note: string;
  base_version: number;
}): Promise<StaffNotesVersion> => {
  const res = await axios.put(`${BACKEND}/v1/admin/staff-notes`, input, { headers: staffAuthHeaders() });
  return res.data?.data?.notes as StaffNotesVersion;
};

export const parseStaffNotes = (content: string): StaffNotesBlock[] =>
  content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line): StaffNotesBlock => {
      if (line.startsWith('## ')) return { kind: 'section', text: line.slice(3).trim() };
      if (line.startsWith('# ')) return { kind: 'part', text: line.slice(2).trim() };
      if (line.startsWith('- ')) return { kind: 'rule', text: line.slice(2).trim() };
      return { kind: 'text', text: line };
    });

export const formatNotesDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** The built-in PDF fonts cannot draw everything a person can paste in. */
const printable = (text: string): string =>
  text
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/[^\x20-\x7E]/g, '');

const PAGE_MARGIN = 50;
const PAGE_BOTTOM = 790;

export const buildStaffNotesPdf = async (notes: StaffNotesVersion) => {
  // Loaded on demand: it is large, and most admin visits never print anything.
  const { default: JsPDF } = await import('jspdf');

  const doc = new JsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const textWidth = pageWidth - PAGE_MARGIN * 2;
  let y = 64;

  const write = (
    text: string,
    options: { size: number; bold?: boolean; gapBefore?: number; bullet?: boolean; keepWithNext?: number }
  ) => {
    const { size, bold = false, gapBefore = 0, bullet = false, keepWithNext = 0 } = options;
    const indent = bullet ? 12 : 0;
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);

    const lines: string[] = doc.splitTextToSize(printable(text), textWidth - indent);
    const lineHeight = size * 1.35;

    // A heading stranded at the foot of a page is worse than the white space.
    if (y + gapBefore + lines.length * lineHeight + keepWithNext > PAGE_BOTTOM) {
      doc.addPage();
      y = 56;
    } else {
      y += gapBefore;
    }

    if (bullet) doc.circle(PAGE_MARGIN + 3, y - 3, 1.2, 'F');
    doc.text(lines, PAGE_MARGIN + indent, y);
    y += lines.length * lineHeight;
  };

  write(STAFF_NOTES_TITLE, { size: 20, bold: true });
  doc.setTextColor(90);
  write(
    `Immersia and GokartGalaxy staff notes. Version ${notes.version}, updated ${formatNotesDate(notes.created_at)} by ${
      notes.changed_by_name
    }.`,
    { size: 9, gapBefore: 2 }
  );
  doc.setTextColor(0);
  y += 6;

  parseStaffNotes(notes.content).forEach((block) => {
    if (block.kind === 'part') {
      write(block.text, { size: 14, bold: true, gapBefore: 16, keepWithNext: 40 });
      doc.setDrawColor(170);
      doc.line(PAGE_MARGIN, y - 10, pageWidth - PAGE_MARGIN, y - 10);
      y += 2;
    } else if (block.kind === 'section') {
      write(block.text, { size: 11, bold: true, gapBefore: 10, keepWithNext: 16 });
    } else if (block.kind === 'rule') {
      write(block.text, { size: 10, gapBefore: 2, bullet: true });
    } else {
      write(block.text, { size: 10, gapBefore: 2 });
    }
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Internal, staff only. Version ${notes.version}.`, PAGE_MARGIN, 815);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - PAGE_MARGIN, 815, { align: 'right' });
  }

  return doc;
};

export const downloadStaffNotesPdf = async (notes: StaffNotesVersion) => {
  const doc = await buildStaffNotesPdf(notes);
  doc.save(`Safety-Rules-and-Closing-Protocol-v${notes.version}.pdf`);
};
